import * as React from 'react';
import _has from 'lodash/has';
import classNames from 'classnames';
import BaseToolbarFeature from './base-toolbar-feature';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { NO_COLOR } from '@src/slide/infrastructure/module/independent/color/constant';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import styles from './base-color-feature.css';
import { PropertyEditorShowAction, ToolbarType } from '@src/js/constant';
import { PaletteMode } from '@com-pc/palette-panel/constants';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import {
    ISlideShowOptions,
} from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import ShapeModel from '@src/slide/domain/ui-model/element-modeles/shape-model';
import PictureModel from '@src/slide/domain/ui-model/element-modeles/picture-model';
import ConnectorModel from '@src/slide/domain/ui-model/element-modeles/connector-model';
import GroupModel from '@src/slide/domain/ui-model/element-modeles/group-model';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { ICTColorDmlA, ISlideDataModel } from '@tencent/slide-sdk-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

const MULTI_COLOR_TYPE = 'multi';

type StoreKeys = 'foreColor' | 'backColor' | 'border-color' | 'fill-color';

// idColorKey的map
const storeColorKeys = {
    foreColor: LocalStorageKeys.FORECOLOR as LocalStorageKeys.FORECOLOR,
    backColor: LocalStorageKeys.BACKCOLOR as LocalStorageKeys.BACKCOLOR,
    ['border-color']: LocalStorageKeys.BORDER_COLOR_RECENT_COLOR as LocalStorageKeys.BORDER_COLOR_RECENT_COLOR,
    ['fill-color']: LocalStorageKeys.FILLCOLOR as LocalStorageKeys.FILLCOLOR,
};

// idThemeKey的map
const storeThemeKeys = {
    foreColor: LocalStorageKeys.FORECOLOR_THEME as LocalStorageKeys.FORECOLOR_THEME,
    backColor: LocalStorageKeys.BACKCOLOR_THEME as LocalStorageKeys.BACKCOLOR_THEME,
    ['border-color']: LocalStorageKeys.BORDERCOLOR_THEME as LocalStorageKeys.BORDERCOLOR_THEME,
    ['fill-color']: LocalStorageKeys.FILLCOLOR_THEME as LocalStorageKeys.FILLCOLOR_THEME,
};

/**
 * 调色板，用于背景色、字体色等
 */
export default class BaseColorFeature extends BaseToolbarFeature {
    constructor() {
        super();
    }

    getDefaultColor = () => {
        switch (this.id) {
            case ActionType.FORECOLOR:
                return new TDColor({ rgbaStr: '#ff0200' });
            case ActionType.BACKCOLOR:
                return new TDColor({ rgbaStr: '#ffff00' });
            case ActionType.FILLCOLOR:
                return new TDColor({ rgbaStr: '#0188fb' });
            case ActionType.BORDERCOLOR:
                return new TDColor({ rgbaStr: '#5c5c5c' });
        }
        return new TDColor({ rgbaStr: '#ffff00' });
    };

    public getRecentColorWithId = (): { tdColor?: TDColor } => {
        const colorStr = userLocalStorage.getItem(storeColorKeys[this.id as StoreKeys], '');
        const tdColorObj = userLocalStorage.getItem(storeThemeKeys[this.id as StoreKeys]);
        let tdColor: TDColor | undefined = undefined;

        // 如果是无填充，则直接返回undefined
        if (colorStr === NO_COLOR) {
            return { tdColor: undefined };
        }

        // else兼容历史数据
        if (tdColorObj) {
            const { themeKey, lumMod, lumOff, srgbColor } = tdColorObj;
            tdColor = new TDColor({
                rgbaStr: srgbColor,
                themeKey,
                lumMod,
                lumOff,
            });
        } else if (colorStr) {
            tdColor = new TDColor({
                rgbaStr: colorStr,
            });
        } else {
            tdColor = this.getDefaultColor();
        }
        return {
            tdColor,
        };
    };

    public saveRecentColorWithId = (tdColor: ITDColor | null) => {
        if (!tdColor) {
            userLocalStorage.setItem(storeColorKeys[this.id as StoreKeys], NO_COLOR);
            userLocalStorage.removeItem(storeThemeKeys[this.id as StoreKeys]);
            return;
        }
        userLocalStorage.setItem(storeColorKeys[this.id as StoreKeys], tdColor.getColorWithNoAlpha());
        if (STSchemeColorVal[tdColor.colorValue]) {
            userLocalStorage.setItem(storeThemeKeys[this.id as StoreKeys], {
                themeKey: tdColor.colorValue as STSchemeColorVal,
                lumMod: tdColor.lumMod,
                lumOff: tdColor.lumOff,
                srgbColor: tdColor.srgb_color,
            });
        } else {
            userLocalStorage.removeItem(storeThemeKeys[this.id as StoreKeys]);
        }
    };

    public onClick(prop?: { defaultColor?: string }) {
        let value;
        let { tdColor } = this.getRecentColorWithId();
        let color;
        if (tdColor) {
            color = tdColor;
        } else if (prop?.defaultColor) {
            tdColor = new TDColor({
                rgbaStr: prop.defaultColor,
            });
            color = tdColor;
        } else {
            color = NO_COLOR;
        }

        const selectedModel = this.getSelectedModel();
        if (this.id === ActionType.FILLCOLOR) {
            value = color === NO_COLOR ? { noFill: 1 } : { solidFill: new SolidFill(tdColor, undefined) };
            selectedModel.getBackgroundFill().updateCurrentFill(value);
            this.twdReport(value);
            return;
        }
        if (this.id === ActionType.BORDERCOLOR) {
            value = color === NO_COLOR ? { color: null, width: 0, style: null, tdColor } : { color, tdColor };
            selectedModel?.getBorder?.()?.updateCurrentBorder(value);
            this.twdReport(value);
            return;
        }

        value = color === NO_COLOR ? null : tdColor;
        this.callback(value);
    }


    /**
     * dc新色盘
     * 通过injector获取
     */
    public getColorPickerDropDownContent = (props: ISlideShowOptions) => {
        const { color } = this.getModeAndValue();
        return <ColorPickerContainer
            {...props}
            selectedTdColor={color}
        />;
    };

    getModelColor = (value) => {
        // let props = this.props;
        let color = typeof (value) === 'string' ? value : '';

        if (_has(value, 'color')) {
            color = value.color;
        }

        return color;
    };

    handleMenuVisibleChange = (visible) => {
        if (visible) {
            setTimeout(() => {
                this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarMenuShown = true;
            }, 50);
        } else {
            this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarMenuShown = false;
        }
    };

    public getColorWithPage(theme) {
        const props: ICTColorDmlA = {
            schemeClr: {
                val: theme.theme?.val,
                egColorTransform: [
                    {
                        lumMod: {
                            val: theme.lum?.lumMod,
                        },
                        lumOff: {
                            val: theme.lum?.lumOff,
                        },
                        tint: {

                        },
                        shade: {

                        },
                    },
                ],
            },
        };
        const slideModel = this.getAppService().viewableServiceLayer.selectionService.currentSlide;
        return SlideDataServices.SDKDataServices.ColorManager()?.resolveColor(
            props,
            slideModel?.sdkData as ISlideDataModel,
        )?.srgbClr?.val || '';
    }

    public getColor() {
        const { tdColor } = this.getRecentColorWithId();
        if (!tdColor) {
            return 'transparent';
        }
        return tdColor?.getColorWithNoAlpha?.();
    }

    public getIcon(containerId, icon) {
        let style;
        const { tdColor } = this.getRecentColorWithId();
        const toolbarMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        const isDoubleToolbar = toolbarMode === ToolbarType.DOUBLE_TOOLBAR;
        if (!tdColor) {
            style = { backgroundColor: 'transparent', border: 'solid 1px rgba(0, 0, 0, 0.24)' };
        } else {
            style = { backgroundColor: tdColor.getColorWithNoAlpha(), border: 'solid 1px rgba(0, 0, 0, 0.24)' };
        }
        return <div id={containerId} style={{ position: 'relative' }}>
            <img src={icon} alt={''}/>
            <div className={classNames({
                [styles[isDoubleToolbar ? 'color-container-double' : 'color-container']]: true,
            })} style={style}></div>
        </div>;
    }

    public getSelectedModel = (): IElementBaseModel => this.getSelectionService()
        .getSelectedModel() as IElementBaseModel;

    public getModeAndValue = (): { mode: number, value: any, color: ITDColor } => {
        let mode;
        let value;
        let color;
        const themeColor = this.getThemeColor();
        if (this.id === ActionType.FILLCOLOR) {
            mode = PaletteMode.FILL;
            value = this.getSelectedModel().getBackgroundFill()
                .getShownColor();
            color = this.getSelectedModel().getBackgroundFill()
                .getShownTdColor();
        } else if (this.id === ActionType.BORDERCOLOR) {
            mode = PaletteMode.BORDER;
            value = this.getSelectedModel()?.getBorder?.()
                .getBorderInfo();
            color = value?.tdColor;
        } else {
            mode = PaletteMode.SIMPLE;
            value = themeColor || undefined;
            color = themeColor || undefined;
        }
        if (value === MULTI_COLOR_TYPE) value = '';
        return { mode, value, color };
    };

    public getThemeColor = (id?: string): ITDColor | undefined | null => {
        switch (id || this.id) {
            case ActionType.FILLCOLOR:
                return this.getSelectedModel().getBackgroundFill()
                    .getFillInfo()?.solidFill?.color;
            case ActionType.BORDERCOLOR:
                return this.getSelectedModel()?.getBorder?.()
                    .getBorderInfo()?.tdColor;
            case ActionType.BACKCOLOR:
                // 突出显示不支持主题
                return new TDColor({
                    rgbaStr: this.getItemValue(ActionType.BACKCOLOR),
                });
            case ActionType.FORECOLOR:
                return this.getItemValue(ActionType.FONT_THEME_COLOR);
            default:
        }
    };

    public checkSelectType = () => {
        const { selectedElements } = this.getAppService().viewableServiceLayer.selectionService;
        const selectedSlideElements = selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        const groupSelectedUIModelArr = selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP);
        if (selectedSlideElements.length > 1) return 3;
        if (selectedSlideElements.length < 1) return 4;
        const elment = selectedSlideElements[0];
        if (elment instanceof ShapeModel || elment instanceof PictureModel || elment instanceof ConnectorModel) {
            return 1;
        } if (elment instanceof GroupModel) {
            if (groupSelectedUIModelArr.length > 1) {
                return 3;
            }
            return 2;
        }
        return 4;
    };

    public handleMoreConfigMenuClick = () => {
        this.getAppService().editableServiceLayer
            ?.rightSideBarManager?.setPropertyEditorShowAction(PropertyEditorShowAction.FILL_MORE_TOGGLE);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_EDIT_FORMAT,
            action: TDWActionType.STUFF_MORE,
        });
    };
}
